import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    resource,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { DecimalPipe, NgTemplateOutlet } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { FlsStore } from "../../../stores/fls.store";
import { AccessLevels } from "../../../pojo/Fls";
import { Invoice } from "../../../pojo/Pricing";
import { Item } from "../../../pojo/Item";
import { Nullable } from "../../../pojo/Util";
import { S25PricingUtil } from "../s25.pricing.util";
import { S25Util } from "../../../util/s25-util";
import { S25Const } from "../../../util/s25-const";
import { s25Resource } from "../../../util/s25.resource";
import { UserprefService } from "../../../services/userpref.service";
import { AriaLive } from "../../../services/aria.live.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { EventMicroService } from "../../../services/event.micro.service";
import { EventListMicroI } from "../../s25-event/EventMicroI";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25DateParse } from "../../../util/core/s25.date.parse";
import { ContactRole, ContactRoleId, OrganizationService } from "../../../services/organization.service";
import { PaymentService } from "../s25-payments/payment.service";
import { EventPricingService } from "../reference-set-pricing/s25.event.pricing.service";
import { AccountSummaryMetrics } from "../reference-set-pricing/account-summary/account-summary-metrics";
import { Table } from "../../s25-table/Table";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25CollapseChevronComponent } from "../../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25ItemComponent } from "../../s25-item/s25.item.component";
import { OrgInvoiceRecords } from "./org-invoice-records";
import { OrgInvoiceMetrics } from "./org-invoice-metrics";
import { OrgInvoiceBatches } from "./org-invoice-batches";
import { ORG_INVOICE_CACHE, OrgInvoiceCache } from "./org-invoice-cache";
import { OrgEventSearch } from "./org-event-search";
import { OrgInvoiceLinkCellComponent } from "./s25.org.invoice.link.cell.component";
import { OrgInvoiceStatusCellComponent } from "./s25.org.invoice.status.cell.component";
import { OrgInvoiceActionsCellComponent } from "./s25.org.invoice.actions.cell.component";
import { S25OrgInvoiceReminderComponent } from "./s25.org.invoice.reminder.component";
import { S25OrgContactEmailComponent } from "./s25.org.contact.email.component";

type StatusFilter = OrgInvoiceRecords.Status | "all";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All statuses" },
    { value: "overdue", label: "Overdue" },
    { value: "outstanding", label: "Outstanding" },
    { value: "paid", label: "Paid" },
];

function idsOf(events: EventListMicroI[]): number[] {
    return S25Util.array.validNumbers(events.map((event) => event.id));
}

/**
 * The date the Due column stands behind. A late row shows the one its lateness was counted from,
 * or "12 days past due" could end up printed beside a date that has not arrived - which it can,
 * where a later payment settled while an earlier one did not.
 */
function dueDateOf(record: OrgInvoiceRecords.Record, daysLate: number): string {
    return daysLate > 0 ? record.unpaidDueDateISO : record.dueDateISO;
}

@TypeManagerDecorator("s25-ng-org-invoices")
@Component({
    selector: "s25-ng-org-invoices",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DecimalPipe,
        NgTemplateOutlet,
        FormsModule,
        S25DatepickerModule,
        S25CollapseChevronComponent,
        S25ButtonComponent,
        S25TableModule,
        S25LoadingInlineStaticComponent,
        S25OrgInvoiceReminderComponent,
        S25OrgContactEmailComponent,
    ],
    providers: [{ provide: ORG_INVOICE_CACHE, useFactory: () => new OrgInvoiceCache() }],
    host: {
        "[attr.id]": "hostId()",
        role: "region",
        "aria-label": "Invoices",
        "[attr.aria-busy]": "loading()",
    },
    template: `
        <div class="acct-head">
            <h2 class="acct-title">
                <button
                    type="button"
                    class="acct-disclosure"
                    [attr.aria-expanded]="metricsOpen()"
                    [attr.aria-controls]="metricsId()"
                    (click)="toggleMetrics()"
                >
                    <s25-ng-collapse-chevron
                        aria-hidden="true"
                        [collapsedOrientation]="'right'"
                        [expanded]="metricsOpen()"
                    />
                    Invoice Summary
                </button>
            </h2>

            @if (loading()) {
                <s25-ng-loading-inline-static [text]="loadingText()" />
            }

            @if (metricsOpen()) {
                <fieldset class="range-picker">
                    <legend class="visually-hidden">Invoice date range</legend>
                    @for (option of rangeOptions; track option) {
                        <label class="range-option" [class.range-option--on]="range() === option">
                            <input
                                type="radio"
                                name="org-invoice-range"
                                class="visually-hidden"
                                [value]="option"
                                [checked]="range() === option"
                                (change)="setRange(option)"
                            />
                            <span>{{ rangeLabel(option) }}</span>
                        </label>
                    }
                </fieldset>
            }
        </div>

        @if (loadError()) {
            <p class="org-invoices--error" role="alert">{{ loadError() }}</p>
        }

        <div [attr.id]="metricsId()" [hidden]="!metricsOpen()">
            <div class="acct-tile-grid" [class.is-stale]="stale()">
                <div class="acct-tile">
                    <div class="acct-tile--label">Total Balance</div>
                    <div class="acct-tile--value">{{ currency(tiles().totalBalance) }}</div>
                </div>
                <div class="acct-tile">
                    <div class="acct-tile--label">Overdue Balance</div>
                    <div class="acct-tile--value" [class.acct-tile--value-alert]="tiles().overdueBalance > 0">
                        {{ currency(tiles().overdueBalance) }}
                    </div>
                </div>
                <div class="acct-tile">
                    <div class="acct-tile--label">Open invoices</div>
                    <div class="acct-tile--value">{{ tiles().openInvoices }}</div>
                </div>
                <div class="acct-tile">
                    <div class="acct-tile--label">Oldest unpaid</div>
                    <div class="acct-tile--value" [class.acct-tile--value-alert]="tiles().oldestUnpaidDays > 0">
                        {{ tiles().oldestUnpaidDays }} <span class="acct-tile--unit">days</span>
                    </div>
                </div>
            </div>

            <div class="inv-overview" [class.is-stale]="stale()">
                <div class="acct-chart-card">
                    <div class="acct-chart-card--label">Billing Contacts</div>
                    <div class="contact-stack">
                        @if (contactsLoading()) {
                            <s25-ng-loading-inline-static [text]="null" />
                        } @else {
                            @for (contact of billingContacts(); track contact.itemId) {
                                <div class="contact-card">
                                    <div class="contact-avatar" aria-hidden="true">
                                        {{ initials(contact.itemName) }}
                                    </div>
                                    <div class="contact-body">
                                        <div class="contact-name-row">
                                            <span class="contact-name">{{ contact.itemName }}</span>
                                            @if (contact.isPrimary) {
                                                <span class="role-chip role-chip--primary">Primary</span>
                                            }
                                        </div>
                                        @if (contact.itemTitle) {
                                            <div class="contact-title">{{ contact.itemTitle }}</div>
                                        }
                                        @if (contact.itemEmail) {
                                            @if (canSendEmail() && tableRecords().length) {
                                                <button
                                                    type="button"
                                                    class="contact-email"
                                                    [attr.aria-label]="'Email ' + contact.itemName"
                                                    (click)="emailContact(contact)"
                                                >
                                                    {{ contact.itemEmail }}
                                                </button>
                                            } @else {
                                                <a class="contact-email" [href]="'mailto:' + contact.itemEmail">{{
                                                    contact.itemEmail
                                                }}</a>
                                            }
                                        }
                                    </div>
                                </div>
                            } @empty {
                                <p class="contact-empty">No billing contacts found</p>
                            }
                        }
                    </div>
                </div>

                <div class="acct-chart-card">
                    <div class="acct-chart-card--label">Amount paid toward total</div>
                    <div class="acct-collect-headline">
                        <span class="acct-collect-pct">{{ collectionPaidPct() | number: "1.0-0" }}%</span>
                        <span class="acct-chart-card--sub">of {{ currency(collection().total) }} billed</span>
                    </div>
                    <div class="acct-collect-bar">
                        <div class="acct-collect-bar--paid" [style.width.%]="collectionPaidPct()"></div>
                        <div class="acct-collect-bar--current" [style.width.%]="collectionCurrentPct()"></div>
                        <div class="acct-collect-bar--overdue" [style.width.%]="collectionOverduePct()"></div>
                    </div>
                    <div class="acct-collect-legend">
                        <div class="acct-collect-legend--row">
                            <span class="acct-collect-swatch acct-collect-swatch--paid"></span>
                            <span>Paid</span>
                            <span class="acct-collect-legend--amt">{{ currency(collection().paid) }}</span>
                            <span class="acct-collect-legend--pct">{{ collectionPaidPct() | number: "1.0-0" }}%</span>
                        </div>
                        <div class="acct-collect-legend--row">
                            <span class="acct-collect-swatch acct-collect-swatch--current"></span>
                            <span>Current</span>
                            <span class="acct-collect-legend--amt">{{ currency(collection().current) }}</span>
                            <span class="acct-collect-legend--pct"
                                >{{ collectionCurrentPct() | number: "1.0-0" }}%</span
                            >
                        </div>
                        <div class="acct-collect-legend--row">
                            <span class="acct-collect-swatch acct-collect-swatch--overdue"></span>
                            <span>Overdue</span>
                            <span class="acct-collect-legend--amt">{{ currency(collection().overdue) }}</span>
                            <span class="acct-collect-legend--pct"
                                >{{ collectionOverduePct() | number: "1.0-0" }}%</span
                            >
                        </div>
                    </div>
                </div>

                <div class="acct-chart-card">
                    <div class="acct-chart-card--label">Balance by age</div>
                    <div class="acct-chart-card--sub">
                        {{ currency(tiles().totalBalance) }} outstanding across {{ tiles().openInvoices }} open invoices
                    </div>
                    <!-- Bars and labels read as one image: apart, the labels announce as bare strings
                     after the summary has already stated every bucket and its amount. -->
                    <div class="age-chart" role="img" [attr.aria-label]="agingSummary()">
                        <div class="age-bars">
                            @for (bucket of agingBuckets(); track bucket.label) {
                                <div class="age-bar">
                                    <span class="age-bar--val">{{ currency(bucket.amount) }}</span>
                                    <div
                                        class="age-bar--fill"
                                        [class.age-bar--fill-overdue]="bucket.overdue"
                                        [style.height.px]="barHeight(bucket.amount)"
                                    ></div>
                                </div>
                            }
                        </div>
                        <div class="age-labels">
                            @for (bucket of agingBuckets(); track bucket.label) {
                                <div class="age-label">{{ bucket.label }}</div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <h2 class="inv-title">Invoices</h2>

        <s25-ng-table
            [class.is-stale]="stale() || holdingRows()"
            [dataSource]="tableConfig()"
            [caption]="tableCaption()"
            [columnSortable]="true"
            [hasFilter]="true"
            [hasRefresh]="true"
            [hasTotalRowCount]="true"
            [showHeaderWhenNoData]="true"
            [noResultsMessage]="'No invoices found for this organization'"
            [pivotThreshold]="700"
            [unlimitedWidth]="true"
        >
            <ng-template #tableHeaderLeft let-filter="filter">
                <ng-container *ngTemplateOutlet="filter" />

                <label class="filter inv-status">
                    Status:
                    <select class="c-input" [ngModel]="statusFilter()" (ngModelChange)="setStatusFilter($event)">
                        @for (option of statusOptions; track option.value) {
                            <option [ngValue]="option.value">{{ option.label }}</option>
                        }
                    </select>
                </label>

                <s25-datepicker [inputLabel]="'From'" [(value)]="rangeFrom" [allowEmpty]="true" />
                <s25-datepicker [inputLabel]="'To'" [(value)]="rangeTo" [allowEmpty]="true" />

                <button type="button" class="aw-button aw-button--primary inv-range-apply" (click)="applyCustomRange()">
                    Apply
                </button>
                @if (customRange()) {
                    <button
                        type="button"
                        class="aw-button aw-button--outline inv-range-clear"
                        (click)="clearCustomRange()"
                    >
                        Clear
                    </button>
                }

                @if (rangeError(); as error) {
                    <span class="inv-range-error" role="alert">{{ error }}</span>
                }
            </ng-template>
        </s25-ng-table>

        @if (totalEventCount()) {
            <div class="inv-more">
                <p #moreCount class="inv-more--count" role="status" tabindex="-1">
                    Showing invoices from {{ loadedEventCount() | number }} of {{ totalEventCount() | number }} events
                    in this range.
                </p>
                @if (hasMoreEvents() || loadingMore()) {
                    <s25-ng-button
                        [type]="'outline'"
                        [buttonText]="'Load more'"
                        [isLoading]="loadingMore()"
                        [loadingText]="'Loading…'"
                        (click)="loadMoreEvents()"
                    />
                }
            </div>
        }

        @if (actionError()) {
            <p class="org-invoices--error" role="alert">{{ actionError() }}</p>
        } @else if (message()) {
            <p class="org-invoices--status">{{ message() }}</p>
        }

        @if (reminder(); as reminder) {
            <s25-ng-org-invoice-reminder
                [orgId]="orgId()"
                [eventId]="reminder.eventId"
                [eventName]="reminder.eventName"
                [invoiceId]="reminder.invoiceId"
                [paymentDetailId]="reminder.paymentDetailId"
                (closed)="closeReminder($event)"
            />
        }

        @if (emailedContact(); as contact) {
            <s25-ng-org-contact-email
                [orgId]="orgId()"
                [contact]="contact"
                [invoices]="tableRecords()"
                (closed)="closeContactEmail()"
            />
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .acct-head {
            display: flex;
            align-items: baseline;
            gap: 14px;
            margin-bottom: 14px;
        }

        .acct-title {
            margin: 0;
        }

        .inv-title {
            margin: 24px 0 14px;
        }

        .acct-disclosure {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0;
            border: none;
            background: none;
            font: inherit;
            color: inherit;
            cursor: pointer;
        }

        .acct-head s25-ng-loading-inline-static {
            display: flex;
            align-self: center;
            font-size: 13px;
            color: var(--color--text-muted);
        }

        .range-picker {
            display: flex;
            margin: 0 0 0 auto;
            padding: 0;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            overflow: hidden;
        }

        :host ::ng-deep .rose-object-table-header .left {
            flex-wrap: wrap;
            row-gap: 0.5em;
            padding-top: 5px;
        }

        :host ::ng-deep .rose-object-table-header .left s25-datepicker .form-group {
            display: flex;
            align-items: center;
            margin: 0;
        }

        :host ::ng-deep .rose-object-table-header .left s25-datepicker .c-input {
            width: 9em;
            min-width: 0;
        }

        .inv-status .c-input {
            width: auto;
        }

        .inv-range-error {
            color: var(--color--text-danger);
        }

        .inv-more {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 12px 0;
        }

        .inv-more--count {
            margin: 0;
            font-size: 13px;
            color: var(--color--text-muted);
        }

        .range-option {
            margin: 0;
            padding: 5px 12px;
            font-size: 13px;
            font-weight: 400;
            cursor: pointer;
            background: var(--color--surface-bg);
            border-left: 1px solid var(--color--border);
        }

        .range-option:first-of-type {
            border-left: none;
        }

        .range-option--on {
            background: var(--color-primary);
            color: #fff;
            font-weight: 600;
        }

        .range-option:has(:focus-visible) {
            outline: 2px solid var(--color--border-bold);
            outline-offset: -3px;
        }

        .org-invoices--status,
        .org-invoices--error {
            margin: 10px 0 0;
            max-width: 100%;
            overflow-wrap: anywhere;
        }

        .org-invoices--status {
            color: var(--color--text-success);
        }

        .org-invoices--error {
            color: var(--color--text-danger);
        }

        .contact-empty {
            margin: 0;
            color: var(--color--text-muted);
        }

        .is-stale {
            opacity: 0.4;
            pointer-events: none;
        }

        .acct-tile-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 14px;
            margin-bottom: 20px;
        }

        .acct-tile {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 15px 16px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            border-radius: 4px;
            min-width: 0;
        }

        .acct-tile--label {
            font-size: 11px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .acct-tile--value {
            font-size: 28px;
            line-height: 1;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }

        .acct-tile--unit {
            font-size: 15px;
            font-weight: 400;
        }

        .acct-tile--value-alert {
            color: var(--color--text-danger);
        }

        .inv-overview {
            display: grid;
            grid-template-columns: 340px 1fr 1fr;
            gap: 14px;
            margin-bottom: 20px;
            align-items: stretch;
        }

        .acct-chart-card {
            display: flex;
            flex-direction: column;
            gap: 14px;
            padding: 16px 18px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            border-radius: 4px;
            min-width: 0;
        }

        .acct-chart-card--label {
            font-size: 11px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .acct-chart-card--sub {
            font-size: 13px;
            color: var(--color--text-muted);
        }

        /* Billing Contacts */
        .contact-stack {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .contact-card {
            display: flex;
            gap: 10px;
            padding: 12px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-bg);
        }

        .contact-avatar {
            flex: none;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: var(--color--primary-flat-bg);
            color: var(--color--primary-flat-fg);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 13px;
            letter-spacing: 0.02em;
        }

        .contact-body {
            display: flex;
            flex-direction: column;
            gap: 3px;
            min-width: 0;
        }

        .contact-name-row {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
        }

        .contact-name {
            font-weight: 600;
            font-size: 0.95rem;
            color: var(--color--text-strong);
        }

        .contact-title {
            font-size: 0.8rem;
            color: var(--color--text-muted);
        }

        .contact-email {
            display: block;
            max-width: 100%;
            padding: 0;
            border: none;
            background: none;
            font-size: 0.85rem;
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color--link-text);
            text-decoration: none;
            cursor: pointer;
        }

        .contact-email:hover,
        .contact-email:focus-visible {
            color: var(--color--link-text-hover);
            text-decoration: none;
        }

        .role-chip {
            font-size: 11px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color--text-muted);
            border: 1px solid var(--color--border);
            border-radius: 999px;
            padding: 0.1em 0.55em;
        }

        .role-chip--primary {
            color: var(--color-primary-darker);
            border-color: color-mix(in srgb, var(--color-primary) 45%, transparent);
            background: color-mix(in srgb, var(--color-primary) 8%, transparent);
        }

        /* Amount paid toward total */
        .acct-collect-headline {
            display: flex;
            align-items: baseline;
            gap: 8px;
        }

        .acct-collect-pct {
            font-size: 36px;
            line-height: 1;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }

        .acct-collect-bar {
            display: flex;
            gap: 2px;
            height: 22px;
            border: 1px solid var(--color--border);
            overflow: hidden;
        }

        .acct-collect-bar--paid {
            background: var(--color-primary);
        }

        .acct-collect-bar--current {
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
            outline: 1px solid var(--color--border-bold);
            outline-offset: -1px;
        }

        .acct-collect-bar--overdue {
            background: repeating-linear-gradient(45deg, var(--color--text-danger) 0 4px, transparent 4px 8px);
        }

        .acct-collect-legend {
            display: flex;
            flex-direction: column;
            gap: 7px;
        }

        .acct-collect-legend--row {
            display: flex;
            align-items: center;
            gap: 9px;
            font-size: 13px;
            border-top: 1px solid var(--color--border);
            padding-top: 7px;
        }

        .acct-collect-legend--amt {
            margin-left: auto;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }

        .acct-collect-legend--pct {
            width: 38px;
            text-align: right;
            color: var(--color--text-muted);
            font-size: 12px;
            font-variant-numeric: tabular-nums;
        }

        .acct-collect-swatch {
            width: 10px;
            height: 10px;
            flex: none;
        }

        .acct-collect-swatch--paid {
            background: var(--color-primary);
        }

        .acct-collect-swatch--current {
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
            outline: 1px solid var(--color--border-bold);
        }

        .acct-collect-swatch--overdue {
            background: repeating-linear-gradient(45deg, var(--color--text-danger) 0 3px, transparent 3px 6px),
                var(--color--surface-bg);
            outline: 1px solid var(--color--text-danger);
        }

        .age-bars {
            display: flex;
            align-items: flex-end;
            gap: 14px;
            height: 175px;
            border-bottom: 1px solid var(--color--border);
        }

        .age-bar {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            gap: 4px;
        }

        .age-bar--val {
            flex: none;
            font-size: 11px;
            line-height: 13px;
            color: var(--color--text-muted);
            font-variant-numeric: tabular-nums;
        }

        .age-bar--fill {
            flex: none;
            width: 26px;
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
            outline: 1px solid var(--color--border-bold);
            outline-offset: -1px;
            border-radius: 4px 4px 0 0;
        }

        .age-bar--fill-overdue {
            background: var(--color--text-danger);
        }

        .age-labels {
            display: flex;
            gap: 14px;
        }

        .age-label {
            flex: 1;
            min-width: 0;
            text-align: center;
            font-size: 11px;
            color: var(--color--text-muted);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        :host ::ng-deep .org-invoices__due--late {
            color: var(--color--text-danger);
        }

        @media (max-width: 900px) {
            .acct-tile-grid {
                grid-template-columns: repeat(2, 1fr);
            }

            .inv-overview {
                grid-template-columns: 1fr;
            }
        }
    `,
})
export class S25OrgInvoicesComponent {
    readonly orgId = input.required<number>();
    readonly orgName = input("");

    private readonly flsStore = inject(FlsStore);
    private readonly pricingService = inject(EventPricingService);

    protected readonly hostId = computed(() => `org-invoices-${this.orgId()}`);
    protected readonly metricsId = computed(() => `${this.hostId()}-summary`);

    protected readonly metricsOpen = signal(true);

    protected readonly rangeFrom = signal<Nullable<number | string | Date>>(null);
    protected readonly rangeTo = signal<Nullable<number | string | Date>>(null);
    protected readonly rangeError = signal("");

    protected readonly canSendEmail = computed(() => this.flsStore.hasAtLeast("MANAGE_PAY", AccessLevels.Read));
    protected readonly canMarkPaid = computed(
        () => this.flsStore.hasFull("EVENT_BILLING") && this.flsStore.hasFull("MANAGE_PAY"),
    );

    // A generic fallback so the Latest Due Date column reads sensibly before the user's real date
    // preference loads, rather than showing a raw ISO string for that first render.
    private readonly dateFormatResource = s25Resource({
        loader: () => UserprefService.getS25Dateformat(),
        defaultValue: "M/d/yy",
    });
    private readonly dateFormat = computed(() => this.dateFormatResource.value());

    private readonly now = S25Util.date.current();

    protected readonly rangeOptions: AccountSummaryMetrics.SummaryRangeOption[] = ["month", "quarter", "ytd"];
    protected readonly range = signal<AccountSummaryMetrics.SummaryRangeOption>("month");
    private readonly dateRange = computed(() => AccountSummaryMetrics.summaryRange(this.range(), this.now));

    /** The table's date range. Metrics data displays up to a year of data, but the table fetches a custom range */
    protected readonly customRange = signal<AccountSummaryMetrics.DateRange | undefined>(undefined);

    private readonly eventsByRange = new Map<string, EventListMicroI[]>();

    private async searchEvents(orgId: number, range: AccountSummaryMetrics.DateRange): Promise<EventListMicroI[]> {
        const search = OrgEventSearch.params(orgId, range);
        const key = `${orgId}:${search.start_dt}:${search.end_dt}`;

        const memoized = this.eventsByRange.get(key);
        if (memoized) return memoized;

        const response = await EventMicroService.getEvents(search);
        const events = (response.content?.data?.items ?? []).slice().sort(OrgEventSearch.byStartDateDesc);
        this.eventsByRange.set(key, events);
        return events;
    }

    private readonly metricEventsResource = s25Resource({
        params: () => ({ orgId: this.orgId(), range: this.dateRange() }),
        loader: ({ params }) => this.searchEvents(params.orgId, params.range),
    });

    private readonly tableEventsResource = s25Resource({
        if: () => !!this.customRange(),
        params: () => ({ orgId: this.orgId(), range: this.customRange()! }),
        loader: ({ params }) => this.searchEvents(params.orgId, params.range),
    });

    private readonly metricEvents = computed(() => this.metricEventsResource.value() ?? []);
    private readonly tableEvents = computed(() => this.tableEventsResource.value() ?? []);

    private readonly metricEventIds = computed(() => idsOf(this.metricEvents()));
    private readonly tableEventIds = computed(() => idsOf(this.tableEvents()));

    /**
     * The table's range is read a page at a time - one page is one `billingCustom.json` read - so a
     * window spanning years costs only what the user asks to see. The search returns newest first,
     * so each page is the next-oldest slice.
     */
    private readonly tablePage = linkedSignal<AccountSummaryMetrics.DateRange | undefined, number>({
        source: () => this.customRange(),
        computation: () => 1,
    });

    protected readSize = OrgInvoiceBatches.SIZE;

    private readonly loadedTableEventIds = computed(() =>
        this.tableEventIds().slice(0, this.tablePage() * this.readSize),
    );

    protected readonly hasMoreEvents = computed(() => this.loadedTableEventIds().length < this.tableEventIds().length);
    protected readonly loadedEventCount = computed(() => this.loadedTableEventIds().length);
    protected readonly totalEventCount = computed(() => this.tableEventIds().length);

    protected readonly loadingMore = linkedSignal<boolean, boolean>({
        source: () => this.loading(),
        computation: (isLoading, previous) => isLoading && !!previous?.value,
    });

    private readonly eventIds = computed(() => [...new Set([...this.metricEventIds(), ...this.loadedTableEventIds()])]);

    private readonly eventNameById = computed(
        () =>
            new Map(
                [...this.metricEvents(), ...this.tableEvents()].map((event) => [
                    event.id ?? 0,
                    event.eventName ?? S25Const.private,
                ]),
            ),
    );

    /**
     * billingCustom returns each invoice's charges and its payments together, so due dates, balances
     * and statuses all come back with the invoices themselves - but it takes every event at once and
     * has no paging
     */
    private readonly invoiceCache = inject(ORG_INVOICE_CACHE);

    private readonly invoicesResource = resource({
        params: () => (this.eventIds().length ? this.eventIds() : undefined),
        stream: async ({ params: eventIds, abortSignal }) => {
            const load = signal<{ value: OrgInvoiceBatches.Load }>({
                value: OrgInvoiceBatches.empty(eventIds.length),
            });
            void OrgInvoiceBatches.walk(
                eventIds,
                this.invoiceCache,
                (batch) => this.pricingService.getInvoices(batch),
                (value) => load.set({ value }),
                abortSignal,
                this.readSize,
            );
            return load;
        },
    });

    private readonly invoiceLoad = computed(() => this.invoicesResource.value());

    private readonly refreshedInvoices = linkedSignal<number[], Invoice[]>({
        source: () => this.eventIds(),
        computation: () => [],
    });

    private readonly records = computed(() =>
        OrgInvoiceRecords.from(
            OrgInvoiceBatches.merge(this.invoiceLoad()?.invoices ?? [], this.refreshedInvoices()),
            this.orgId(),
            this.eventNameById(),
        ).sort(OrgInvoiceRecords.byDueDateDesc),
    );

    /**
     * The rows belonging to one of the two ranges. The cache is what says which those are: it files
     * every invoice under an event that was asked for, so an invoice billing several events - or
     * naming none - lands in the range that read it instead of being sorted out by a date it has.
     */
    private recordsOf(eventIds: number[]): OrgInvoiceRecords.Record[] {
        const invoiceIds = new Set(this.invoiceCache.read(eventIds).map((invoice) => invoice.id));
        return this.records().filter((record) => invoiceIds.has(record.id));
    }

    private readonly metricRecords = computed(() => this.recordsOf(this.metricEventIds()));
    protected readonly tableRecords = computed(() =>
        this.customRange() ? this.recordsOf(this.loadedTableEventIds()) : this.metricRecords(),
    );

    /**
     * The rows the table draws from. While a fetch is out the ones already on screen are held rather
     * than cleared: emptying the table takes their height out of the page, and the browser clamps the
     * user's scroll position to what is left, which reads as the page jumping to the top.
     */
    private readonly heldRecords = linkedSignal<
        { loading: boolean; records: OrgInvoiceRecords.Record[] },
        OrgInvoiceRecords.Record[]
    >({
        source: () => ({ loading: this.loading(), records: this.tableRecords() }),
        computation: ({ loading, records }, previous) =>
            loading && !records.length ? (previous?.value ?? []) : records,
    });

    /** Held rows describe the range the user has left, so they are dimmed rather than passed off. */
    protected readonly holdingRows = computed(
        () => this.loading() && !this.tableRecords().length && !!this.heldRecords().length,
    );

    protected readonly statusOptions = STATUS_OPTIONS;
    protected readonly statusFilter = signal<StatusFilter>("all");

    protected readonly visibleRecords = computed(() => {
        const status = this.statusFilter();
        if (status === "all") return this.heldRecords();

        return this.heldRecords().filter((record) => OrgInvoiceMetrics.status(record, this.now) === status);
    });

    private readonly batchesPending = computed(() => {
        const load = this.invoiceLoad();
        return !!this.eventIds().length && (!load || load.loadedEvents < load.totalEvents);
    });

    protected readonly loading = computed(
        () => this.metricEventsResource.isLoading() || this.tableEventsResource.isLoading() || this.batchesPending(),
    );

    protected readonly loadingText = computed(() => {
        const load = this.invoiceLoad();
        if (!load?.loadedEvents) return "Loading invoices…";
        return `Loading invoices… ${load.loadedEvents} of ${load.totalEvents} events`;
    });

    /** Nothing has arrived yet, so the figures below still describe the previous range. */
    protected readonly stale = computed(() => this.loading() && !this.invoiceLoad()?.loadedEvents);

    protected readonly loadError = computed(() => {
        const eventsFailure = this.metricEventsResource.error() ?? this.tableEventsResource.error();
        if (eventsFailure) return S25Util.errorText(eventsFailure, S25Const.defaultErrorMessage);

        const load = this.invoiceLoad();
        if (!load?.failedBatches) return "";

        const reported = S25Util.errorText(load.failure, S25Const.defaultErrorMessage);
        const short = load.invoices.length ? " Some invoices could not be read." : "";
        return `${reported}${short} Try a shorter date range.`;
    });

    private readonly contactsResource = s25Resource({
        params: () => this.orgId(),
        loader: ({ params: orgId }) => OrganizationService.getContactsForOrgs([orgId]),
    });
    protected readonly contactsLoading = computed(() => this.contactsResource.isLoading());
    protected readonly billingContacts = computed(() =>
        (this.contactsResource.value() ?? []).filter(
            (contact: ContactRole) => contact.role?.itemId === ContactRoleId.BillingContact,
        ),
    );

    protected readonly message = signal("");
    protected readonly actionError = signal("");
    /** The row whose compose panel is open, if any. */
    protected readonly reminder = signal<
        { invoiceId: number; eventId: number; eventName: string; paymentDetailId?: number } | undefined
    >(undefined);
    /** The billing contact the compose panel is addressed to, when it was opened from the card. */
    protected readonly emailedContact = signal<ContactRole | undefined>(undefined);
    /** In process of marking a payment as paid **/
    private readonly marking = signal(false);

    private readonly table = viewChild(S25TableComponent);
    private readonly moreCount = viewChild<ElementRef<HTMLElement>>("moreCount");

    /** Whether the previous effect run had a page in flight, so its landing can be acted on once. */
    private wasLoadingMore = false;

    constructor() {
        TelemetryService.sendWithSub("Pricing", "Org", "InvoicesTab");

        effect(() => {
            this.visibleRecords();
            this.canSendEmail();
            this.canMarkPaid();
            this.dateFormat();
            untracked(() => {
                const table = this.table();
                // This effect's first run lands inside the change detection that creates the table,
                // before its dataSource binding is applied - there is nothing to re-fetch yet.
                if (table?.dataSource) void table.refresh(false, false, true);
            });
        });

        // The last page takes the Load more button away from under the user, which would drop focus
        // to the body. The count outlives it and reports what landed, so focus goes there.
        effect(() => {
            const loadingMore = this.loadingMore();
            const hasMore = this.hasMoreEvents();
            untracked(() => {
                if (this.wasLoadingMore && !loadingMore && !hasMore) {
                    this.moreCount()?.nativeElement.focus();
                }
                this.wasLoadingMore = loadingMore;
            });
        });
    }

    protected rangeLabel(option: AccountSummaryMetrics.SummaryRangeOption): string {
        return AccountSummaryMetrics.summaryRangeLabel(option);
    }

    protected setRange(option: AccountSummaryMetrics.SummaryRangeOption) {
        if (option === this.range()) return;
        this.range.set(option);
        TelemetryService.sendWithSub("Pricing", "Org", "InvoicesRange" + S25Util.firstCharToUpper(option));
        void AriaLive.announce(`Showing ${this.rangeLabel(option)}`);
    }

    protected toggleMetrics() {
        const open = !this.metricsOpen();
        this.metricsOpen.set(open);
        void AriaLive.announce(open ? "Invoice summary expanded" : "Invoice summary collapsed");
    }

    protected applyCustomRange() {
        const from = S25Util.date.parse(this.rangeFrom());
        const to = S25Util.date.parse(this.rangeTo());
        if (!from || !to) return this.rangeError.set("Enter both a start and an end date.");
        if (from > to) return this.rangeError.set("The start date must come before the end date.");

        this.rangeError.set("");
        this.customRange.set({ start: S25Util.date.toStartOfDay(from), end: S25Util.date.toEndOfDay(to) });
        TelemetryService.sendWithSub("Pricing", "Org", "InvoicesCustomRange");
        void AriaLive.announce("Showing invoices for the selected dates");
    }

    protected clearCustomRange() {
        this.rangeError.set("");
        this.rangeFrom.set(null);
        this.rangeTo.set(null);
        this.customRange.set(undefined);
        void AriaLive.announce(`Showing ${this.rangeLabel(this.range())}`);
    }

    protected loadMoreEvents() {
        if (this.loading() || !this.hasMoreEvents()) return;
        this.loadingMore.set(true);
        this.tablePage.update((page) => page + 1);
    }

    protected setStatusFilter(status: StatusFilter) {
        this.statusFilter.set(status);
        void AriaLive.announce(`Showing ${STATUS_OPTIONS.find((option) => option.value === status)?.label}`);
    }

    protected readonly tiles = computed(() => OrgInvoiceMetrics.tiles(this.metricRecords(), this.now));
    protected readonly collection = computed(() => OrgInvoiceMetrics.collectionSplit(this.metricRecords(), this.now));
    protected readonly collectionPaidPct = computed(() =>
        OrgInvoiceMetrics.percentOf(this.collection().paid, this.collection().total),
    );
    protected readonly collectionCurrentPct = computed(() =>
        OrgInvoiceMetrics.percentOf(this.collection().current, this.collection().total),
    );
    protected readonly collectionOverduePct = computed(() =>
        OrgInvoiceMetrics.percentOf(this.collection().overdue, this.collection().total),
    );

    protected readonly agingBuckets = computed(() => OrgInvoiceMetrics.agingBuckets(this.metricRecords(), this.now));
    private readonly agingMax = computed(() => Math.max(1, ...this.agingBuckets().map((bucket) => bucket.amount)));
    protected readonly agingSummary = computed(
        () =>
            "Balance by age: " +
            this.agingBuckets()
                .map((bucket) => `${bucket.label} ${this.currency(bucket.amount)}`)
                .join("; "),
    );

    protected readonly tableCaption = computed(() => `Invoices for ${this.orgName() || "this organization"}`);
    protected readonly tableConfig = computed<Table.DataSource>(() => ({
        type: "unpaginated",
        columns: this.columns(),
        dataSource: (query: Table.UnpaginatedQuery) => {
            if (query.forceRefresh) this.reloadInvoices();
            return Promise.resolve({ rows: this.buildRows() });
        },
    }));

    protected readonly currency = S25PricingUtil.formatCurrency;

    protected barHeight(amount: number): number {
        return Math.max(2, Math.round((amount / this.agingMax()) * 150));
    }

    protected initials(name: string): string {
        const parts = name.trim().split(/\s+/);
        return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
    }

    private columns(): Table.Column[] {
        return [
            { header: "Invoice Name", id: "name", sortable: true },
            { header: "Invoice Number", id: "number", sortable: true, width: 140 },
            { header: "Event Name", id: "event", sortable: true },
            { header: "Latest Due Date", id: "due", sortable: true },
            { header: "Status", id: "status", sortable: true, width: 130 },
            { header: "Balance", id: "balance", sortable: true, align: "right", width: 120 },
            { header: "Actions", id: "actions", width: 220, export: false },
        ];
    }

    private buildRows(): Table.Row[] {
        const now = this.now;

        return this.visibleRecords().map((record): Table.Row => {
            const status = OrgInvoiceMetrics.status(record, now);
            const balance = OrgInvoiceMetrics.balance(record);
            const daysLate = OrgInvoiceMetrics.daysLate(record, now);
            const hasOutstandingPayment = !!record.outstandingPaymentIds.length;
            // A follow-up only sends mail; a request logs a new payment, so it takes the same right as marking one paid.
            const canEmail = hasOutstandingPayment ? this.canSendEmail() : this.canMarkPaid();

            return {
                id: record.id,
                name: record.invoiceName,
                cells: {
                    name: {
                        component: OrgInvoiceLinkCellComponent,
                        inputs: {
                            label: record.invoiceName,
                            eventId: record.eventId,
                            invoiceId: record.id,
                            elementId: this.invoiceLinkId(record),
                        },
                        textValue: record.invoiceName,
                    } satisfies Table.Cell<OrgInvoiceLinkCellComponent>,
                    number: { text: record.invoiceNumber },
                    event: {
                        component: S25ItemComponent,
                        inputs: { itemTypeId: Item.Ids.Event, itemId: record.eventId, itemName: record.eventName },
                        textValue: record.eventName,
                    } satisfies Table.Cell<S25ItemComponent>,
                    due: {
                        text: this.dueDateText(record, daysLate),
                        sortValue: dueDateOf(record, daysLate),
                        className: daysLate > 0 ? "org-invoices__due--late" : "",
                    },
                    status: {
                        component: OrgInvoiceStatusCellComponent,
                        inputs: { status },
                        // Sorted by severity (overdue first), not alphabetically.
                        sortValue: { overdue: "0", outstanding: "1", paid: "2" }[status],
                        textValue: status,
                    } satisfies Table.Cell<OrgInvoiceStatusCellComponent>,
                    balance: {
                        text: this.currency(balance),
                        // Intl.Collator compares lexically, so a currency string needs a zero-padded
                        // numeric sort key or "$1,200.00" sorts before "$600.00".
                        sortValue: String(Math.round(balance * 100)).padStart(12, "0"),
                        align: "right",
                    },
                    actions: {
                        component: OrgInvoiceActionsCellComponent,
                        inputs: {
                            invoiceNumber: record.invoiceNumber || record.invoiceName,
                            // A settled invoice is owed nothing, so there is neither mail to send
                            // nor a payment to mark - the cell falls through to a dash.
                            canSendEmail: canEmail && status !== "paid",
                            canMarkPaid: this.canMarkPaid() && hasOutstandingPayment,
                            hasOutstandingPayment,
                            onSendEmail: () => this.sendEmail(record),
                            onMarkPaid: () => this.markAsPaid(record),
                        },
                    } satisfies Table.Cell<OrgInvoiceActionsCellComponent>,
                },
            };
        });
    }

    private dueDateText(record: OrgInvoiceRecords.Record, daysLate: number): string {
        const dueDateISO = dueDateOf(record, daysLate);
        const formatted =
            S25Datefilter.transform(S25DateParse.parseDropTZLenient(dueDateISO), this.dateFormat() ?? "") ?? dueDateISO;
        if (daysLate <= 0) return formatted;
        return `${formatted} — ${daysLate} day${daysLate === 1 ? "" : "s"} past due`;
    }

    protected sendEmail(record: OrgInvoiceRecords.Record) {
        const paymentDetailId = record.outstandingPaymentIds[0];
        this.emailedContact.set(undefined);
        this.reminder.set({
            invoiceId: record.id,
            eventId: record.eventId,
            eventName: record.eventName,
            paymentDetailId,
        });

        const action = paymentDetailId ? "a follow-up" : "a payment request";
        void AriaLive.announce(`Compose ${action} for ${this.recordLabel(record)}`);
    }

    protected emailContact(contact: ContactRole) {
        this.reminder.set(undefined);
        this.emailedContact.set(contact);
        void AriaLive.announce(`Compose an email to ${contact.itemName}`);
    }

    protected closeContactEmail() {
        this.emailedContact.set(undefined);
    }

    /**
     * A sent request logs a payment against the sidebar's own scoped store, which reloads itself
     * afterwards - so the invoices it closes on are already current, and are taken as they are
     * rather than read back over a request the tab has just paid for once.
     */
    protected closeReminder(invoices: Invoice[]) {
        const reminder = this.reminder();
        this.reminder.set(undefined);
        if (!reminder) return;

        this.holdInvoicesOf(reminder.eventId, invoices);
        this.focusInvoiceLink(reminder.invoiceId);
    }

    /**
     * Reads one event's invoices again and holds them over the batch walk's copy. A write only ever
     * touches the invoice the user acted on, so replaying every batch to see it would be a whole
     * range of requests to pick up a single change.
     */
    private async refreshEvent(eventId: number): Promise<void> {
        const loadedFor = this.eventIds();
        const [billing] = await S25Util.Maybe(this.pricingService.getInvoices([eventId]));
        if (!billing) return;

        this.invoiceCache.record([eventId], billing);
        this.holdInvoicesOf(eventId, billing.data?.items ?? [], loadedFor);
    }

    /**
     * Holds one event's invoices over the batch walk's copy of them, wherever they came from. They
     * are cached as well, or a switch away and back would hand the user the copy from before.
     */
    private holdInvoicesOf(eventId: number, fresh: Invoice[], loadedFor = this.eventIds()) {
        this.invoiceCache.recordInvoices([eventId], fresh);

        // The range may have changed while the write was going through, which is a different set of
        // invoices entirely - and one these have no business being merged into.
        if (this.eventIds() !== loadedFor) return;
        this.refreshedInvoices.update((held) => OrgInvoiceBatches.merge(held, fresh));
    }

    private reloadInvoices() {
        this.invoiceCache.clear();
        this.eventsByRange.clear();
        this.refreshedInvoices.set([]);
        this.tablePage.set(1);
        this.metricEventsResource.resource.reload();
        this.tableEventsResource.resource.reload();
    }

    protected async markAsPaid(record: OrgInvoiceRecords.Record) {
        if (this.marking()) return;
        this.marking.set(true);
        this.actionError.set("");

        const [, error] = await S25Util.Maybe(
            Promise.all(record.outstandingPaymentIds.map((id) => PaymentService.setPaid(id))),
        );

        if (error) {
            this.marking.set(false);
            this.actionError.set(S25Util.errorText(error, S25Const.defaultErrorMessage));
            return;
        }

        await this.refreshEvent(record.eventId);
        this.marking.set(false);

        this.focusInvoiceLink(record.id);
        const announcement = `${this.recordLabel(record)} marked as paid`;
        this.message.set(announcement);
        void AriaLive.announce(announcement);
    }

    private recordLabel(record: OrgInvoiceRecords.Record): string {
        return record.invoiceNumber || record.invoiceName;
    }

    private invoiceLinkId(record: OrgInvoiceRecords.Record): string {
        return `${this.hostId()}-link-${record.id}`;
    }

    private focusInvoiceLink(invoiceId: number) {
        document.getElementById(`${this.hostId()}-link-${invoiceId}`)?.focus();
    }
}
